import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import useMediaQuery from '../../../hooks/useMediaQuery';
import useTablistKeyboard from '../../../hooks/useTablistKeyboard';

import useScrollOverflow, { revealWithinStrip } from './useScrollOverflow';

import './Cockpit.css';

// Below this width AdaptiveSubnav lays out as a horizontal strip (see Cockpit.css).
export const SUBNAV_HORIZONTAL_QUERY = '(max-width: 639px)';

const metricDescription = ({ label, value, detail }) => (
  [label, value, detail]
    .filter((part) => part !== undefined && part !== null && part !== '')
    .join(' · ')
);

export const CockpitStatusCard = ({
  eyebrow,
  badge,
  title,
  description,
  metrics = [],
  visuals,
  status = 'neutral',
  className = '',
  children,
  style,
  ...props
}) => {
  if (children) {
    return (
      <section
        className={`admin-cockpit-status-card ${className}`.trim()}
        data-status={status}
        style={style}
        {...props}
      >
        {children}
      </section>
    );
  }

  const hasCopy = Boolean(eyebrow || badge || title || description);

  return (
    <section
      className={`admin-cockpit-status-card ${className}`.trim()}
      data-status={status}
      data-has-copy={hasCopy ? 'true' : 'false'}
      data-has-visuals={visuals ? 'true' : 'false'}
      data-metric-count={metrics.length}
      style={{ '--cockpit-metric-count': Math.max(metrics.length, 1), ...style }}
      {...props}
    >
      {hasCopy && (
        <div className="admin-cockpit-status-copy">
          {(eyebrow || badge) && (
            <div className="admin-cockpit-status-kicker">
              <span className="admin-cockpit-status-dot" aria-hidden="true" />
              {eyebrow && <span>{eyebrow}</span>}
              {badge && <small>{badge}</small>}
            </div>
          )}
          {title && <h2>{title}</h2>}
          {description && <p>{description}</p>}
        </div>
      )}
      {metrics.length > 0 && (
        <div className="admin-cockpit-status-metrics">
          {metrics.map((metric) => {
            const Icon = metric.icon;

            return (
              <div
                className="admin-cockpit-status-metric"
                data-tone={metric.tone || undefined}
                data-metric-id={metric.id || undefined}
                key={metric.id || metric.label}
                // Labels/details may ellipsize in compact layouts; the full
                // reading stays available on hover and to assistive tech.
                title={metricDescription(metric)}
              >
                <span className="admin-cockpit-status-metric-label">
                  {Icon && <Icon className="admin-cockpit-status-metric-icon" aria-hidden="true" />}
                  {metric.label}
                </span>
                <strong>{metric.value}</strong>
                {metric.detail && <small>{metric.detail}</small>}
              </div>
            );
          })}
        </div>
      )}
      {visuals && <div className="admin-cockpit-status-visuals">{visuals}</div>}
    </section>
  );
};

// Pointer affordances never take focus, so paging leaves focus where it was.
const keepFocus = (event) => event.preventDefault();

/**
 * Edge fades + previous/next buttons for a horizontally scrolling strip.
 * The buttons are pointer affordances (tabIndex -1, no focus on press):
 * keyboard users move through the tablist with arrow keys, which already
 * reveal the selected tab. If a control is focused anyway (e.g. by assistive
 * tech) and then hides at the strip edge, focus moves to the selected item,
 * revealed inside the strip, instead of staying on an invisible button.
 */
const StripOverflowControls = ({ overflow, label, activeElementId, stripRef }) => {
  const startRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    const focused = document.activeElement;
    const hiddenFocus = (!overflow.start && focused === startRef.current)
      || (!overflow.end && focused === endRef.current);
    if (!hiddenFocus) return;
    const item = document.getElementById(activeElementId);
    revealWithinStrip(stripRef.current, item, 'x');
    item?.focus({ preventScroll: true });
  }, [activeElementId, overflow.end, overflow.start, stripRef]);

  return (
    <>
      <button
        ref={startRef}
        type="button"
        className="admin-cockpit-strip-control"
        data-edge="start"
        data-visible={overflow.start ? 'true' : 'false'}
        aria-label={`Show previous ${label}`}
        aria-hidden={overflow.start ? undefined : 'true'}
        tabIndex={-1}
        onMouseDown={keepFocus}
        onClick={() => overflow.scrollByPage('start')}
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      </button>
      <button
        ref={endRef}
        type="button"
        className="admin-cockpit-strip-control"
        data-edge="end"
        data-visible={overflow.end ? 'true' : 'false'}
        aria-label={`Show more ${label}`}
        aria-hidden={overflow.end ? undefined : 'true'}
        tabIndex={-1}
        onMouseDown={keepFocus}
        onClick={() => overflow.scrollByPage('end')}
      >
        <ChevronRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </>
  );
};

// Keep the selected item inside its strip whenever the selection changes
// (route change, Back/Forward, remembered place, or keyboard) and whenever the
// strip itself is resized (viewport, sidebar mode, or display size changes).
const useRevealActive = (stripRef, activeElementId, enabled, measure) => {
  useEffect(() => {
    const strip = stripRef.current;
    if (!enabled || !strip) return undefined;

    const reveal = () => {
      const item = activeElementId ? document.getElementById(activeElementId) : null;
      revealWithinStrip(strip, item, 'x');
      measure();
    };
    reveal();

    window.addEventListener('resize', reveal);
    const created = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(reveal);
    const observer = typeof created?.observe === 'function' ? created : null;
    observer?.observe(strip);

    return () => {
      window.removeEventListener('resize', reveal);
      observer?.disconnect?.();
    };
  }, [activeElementId, enabled, measure, stripRef]);
};

export const CockpitTabs = ({ tabs, active, onChange, label, idPrefix }) => {
  const stripRef = useRef(null);
  const tabIds = useMemo(() => tabs.map((tab) => tab.id), [tabs]);
  const buildId = (id) => `${idPrefix}-${id}`;
  const onKeyDown = useTablistKeyboard({
    tabIds,
    activeId: active,
    onSelect: onChange,
    buildId,
  });
  const overflow = useScrollOverflow(stripRef);
  useRevealActive(stripRef, active ? buildId(active) : null, true, overflow.measure);

  return (
    <div
      className="admin-cockpit-tabs-frame"
      data-overflow-start={overflow.start ? 'true' : 'false'}
      data-overflow-end={overflow.end ? 'true' : 'false'}
    >
      <nav ref={stripRef} className="admin-cockpit-tabs" role="tablist" aria-label={label}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const selected = tab.id === active;

          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={buildId(tab.id)}
              aria-selected={selected}
              aria-controls={`${idPrefix}-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              className={`admin-cockpit-tab${selected ? ' is-active' : ''}`}
              onClick={() => onChange(tab.id)}
              onKeyDown={onKeyDown}
            >
              {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
              <span>{tab.label}</span>
            </button>
          );
        })}
      </nav>
      <StripOverflowControls
        overflow={overflow}
        label="sections"
        activeElementId={active ? buildId(active) : null}
        stripRef={stripRef}
      />
    </div>
  );
};

export const AdaptiveSubnav = ({ tabs, active, onChange, label, idPrefix }) => {
  const rootRef = useRef(null);
  const [engaged, setEngaged] = useState(false);
  // On phones the rail becomes a horizontal strip, so the ARIA orientation
  // and arrow keys follow the layout instead of staying vertical.
  const horizontal = useMediaQuery(SUBNAV_HORIZONTAL_QUERY);
  const orientation = horizontal ? 'horizontal' : 'vertical';
  const tabIds = useMemo(() => tabs.map((tab) => tab.id), [tabs]);
  const buildId = (id) => `${idPrefix}-${id}`;
  const onKeyDown = useTablistKeyboard({
    tabIds,
    activeId: active,
    onSelect: onChange,
    buildId,
    orientation,
  });
  const overflow = useScrollOverflow(rootRef, { enabled: horizontal });
  useRevealActive(rootRef, active ? buildId(active) : null, horizontal, overflow.measure);

  useEffect(() => {
    if (!engaged) return undefined;

    const collapseOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setEngaged(false);
    };

    document.addEventListener('pointerdown', collapseOutside, true);
    return () => document.removeEventListener('pointerdown', collapseOutside, true);
  }, [engaged]);

  return (
    <div
      className="admin-cockpit-subnav-frame"
      data-orientation={orientation}
      data-overflow-start={overflow.start ? 'true' : 'false'}
      data-overflow-end={overflow.end ? 'true' : 'false'}
    >
      <nav
        ref={rootRef}
        className="admin-cockpit-subnav"
        role="tablist"
        aria-label={label}
        aria-orientation={orientation}
        data-expanded={engaged ? 'true' : 'false'}
        onPointerDown={(event) => {
          if (event.pointerType !== 'mouse') setEngaged(true);
        }}
        onFocus={() => setEngaged(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setEngaged(false);
        }}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const selected = tab.id === active;

          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={buildId(tab.id)}
              aria-selected={selected}
              aria-controls={`${idPrefix}-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              title={tab.label}
              className={`admin-cockpit-subnav-item${selected ? ' is-active' : ''}`}
              onClick={() => onChange(tab.id)}
              onKeyDown={onKeyDown}
            >
              <span className="admin-cockpit-subnav-icon">
                {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
              </span>
              <span className="admin-cockpit-subnav-label">{tab.label}</span>
            </button>
          );
        })}
      </nav>
      {horizontal && (
        <StripOverflowControls
          overflow={overflow}
          label={label.toLowerCase()}
          activeElementId={active ? buildId(active) : null}
          stripRef={rootRef}
        />
      )}
    </div>
  );
};

export const CockpitWorkspace = ({ children, className = '', ...props }) => (
  <div className={`admin-cockpit-workspace ${className}`.trim()} {...props}>
    {children}
  </div>
);

export const CockpitWorkspaceContent = ({ children, className = '', ...props }) => (
  <div className={`admin-cockpit-workspace-content ${className}`.trim()} {...props}>
    {children}
  </div>
);

/**
 * The selected primary-tab panel. It sits directly under CockpitTabs and is
 * the cockpit's one scroll region: when CockpitHome gives the workspace a
 * finite height (data-scroll-owner="workspace") this element owns vertical
 * scrolling for long editors and tables; otherwise the document does. It is
 * focusable so keyboard users can scroll it without a pointer.
 */
export const CockpitPanel = ({ children, className = '', ...props }) => (
  <div
    role="tabpanel"
    tabIndex={0}
    className={`admin-cockpit-panel ${className}`.trim()}
    data-cockpit-panel=""
    {...props}
  >
    {children}
  </div>
);
